/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button } from 'antd';
import { useEffect, useState } from 'react';
import { SyncOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { matchPath } from 'react-router';
import { getOrderDetail } from '../../../../api';
import { fetchEnvCollectionList } from '../../../../api/game/env';
import { AutoTable } from '../../../../components/auto-table';
import { getLocalTime, getPrice } from '../../../../lib';
import getFormatMessage from '../../../../locales/get-format-message';
import {
  IApiOrderListItem,
  OrderStatus,
  OrderType,
} from '../../../../model/api-modules';
import { IApiGameEnvCollectionRes } from '../../../../model/api-modules/env';
import { IRootState } from '../../../../store/reducers';
import { OrderStatusConfig } from '../../../studio/lib/service-order';
import styles from './index.module.scss';
import { openContactMethod } from '../../../../lib/email';
import classNames from 'classnames';

export const OrderDetail = () => {
  const [detail, setDetail] = useState<IApiOrderListItem>();
  const [reloading, setReloading] = useState<boolean>(false);
  const selectStudio = useSelector(
    (state: IRootState) => state.studio.selectStudio
  );
  const [gameEnvs, setGameEnvs] = useState<readonly IApiGameEnvCollectionRes[]>(
    []
  );
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const matched = matchPath<{ orderID: string }>(
    location.pathname,
    '/studio/:studioId/fee/order-manage/detail/:orderID'
  );

  useEffect(() => {
    init();
  }, [selectStudio?.studioID]);

  const init = async () => {
    if (!selectStudio) {
      return;
    }

    const orderID = matched?.params.orderID;
    const gameEnvRes = await fetchEnvCollectionList(selectStudio.studioID);
    if (gameEnvRes) {
      setGameEnvs(gameEnvRes);
    }

    if (orderID) {
      const res = await getOrderDetail(orderID);
      setDetail(res);
    }
  };

  const reload = async () => {
    setReloading(true);
    await init();
    setReloading(false);
  };
  if (!detail) {
    return null;
  }

  const renderStatus = () => {
    const renderConfig = OrderStatusConfig[detail.order_status];
    if (renderConfig) {
      return (
        <>
          <div>
            {renderConfig.icon}
            <span>{renderConfig.title}</span>
          </div>
          <div>
            <Button
              className={classNames('gc-btn', 'gc-btn-default')}
              onClick={(event) => openContactMethod(event)}
            >
              {getFormatMessage('order_contact_us')}
            </Button>
            <Button
              className={classNames(
                'gc-btn',
                'gc-btn-default',
                styles.refreshBtn
              )}
              onClick={reload}
            >
              <SyncOutlined spin={reloading} />
              {getFormatMessage('refresh')}
            </Button>
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <div className={styles.detailPage}>
      <div className={styles.topCard}>
        <div className={styles.title}>{renderStatus()}</div>
        <div className={styles.topDetail}>
          <div>
            <label>{getFormatMessage('order_id')}</label>
            <div>{detail.order_id}</div>
          </div>
          <div>
            <label>{getFormatMessage('order_type')}</label>
            <div>
              {
                {
                  [OrderType.New]: getFormatMessage('order_new_pay'),
                  [OrderType.Renew]: getFormatMessage('order_renew'),
                }[detail.order_type]
              }
            </div>
          </div>
          <div>
            <label>{getFormatMessage('order_creator')}</label>
            <div>{detail.order_creator}</div>
          </div>
          <div>
            <label>{getFormatMessage('order_create_time')}</label>
            <div>
              {getLocalTime(
                detail.create_time,
                userInfo?.UserSettings?.Timezone
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.tableCard}>
        <AutoTable
          list={[detail]}
          configFields={[
            {
              key: 'order_id',
              name: getFormatMessage('order_sub_id'),
            },
            {
              key: 'service_name',
              name: getFormatMessage('order_produce'),
            },
            {
              key: 'game_id',
              name: getFormatMessage('order_game'),
              tableExtension: {
                renderRow(key, value, row) {
                  const order = row as IApiOrderListItem;
                  const matchEnv = gameEnvs.find((env) =>
                    new RegExp(`${order.game_id}-\\d+-${order.env_id}`).test(
                      env.envGuid
                    )
                  );
                  if (matchEnv) {
                    return <span>{matchEnv.gameName}</span>;
                  }
                  return null;
                },
              },
            },
            {
              key: 'env_id',
              name: getFormatMessage('order_env'),
              tableExtension: {
                renderRow(key, value, row) {
                  const order = row as IApiOrderListItem;
                  const matchEnv = gameEnvs.find((env) =>
                    new RegExp(`${order.game_id}-\\d+-${order.env_id}`).test(
                      env.envGuid
                    )
                  );
                  if (matchEnv) {
                    return <span>{matchEnv.envName}</span>;
                  }
                  return null;
                },
              },
            },
            {
              key: 'order_mode',
              name: getFormatMessage('order_fee_mode'),
              tableExtension: {
                renderRow(key, value) {
                  return getFormatMessage('order_post_fee');
                },
              },
            },
            {
              key: 'freeze_amount',
              name: getFormatMessage('order_freeze_amount'),
              tableExtension: {
                renderRow(key, value) {
                  return getPrice(value).amount;
                },
              },
            },
          ]}
          layoutExtension={{
            showTable: true,
            showTopbar: false,
            showFilter: true,
          }}
          onChangedFilterValues={(val) => {
            console.log(val);
          }}
        />
      </div>
    </div>
  );
};
